import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  Newspaper, Loader2, Sparkles, Trash2, Eye, Calendar, Settings,
  Save, CheckCircle2, BarChart3, Target, Users, Share2
} from "lucide-react";

const DAYS = [
  { value: "lundi", label: "Lundi" },
  { value: "mardi", label: "Mardi" },
  { value: "mercredi", label: "Mercredi" },
  { value: "jeudi", label: "Jeudi" },
  { value: "vendredi", label: "Vendredi" },
  { value: "samedi", label: "Samedi" },
  { value: "dimanche", label: "Dimanche" },
];

export function MarketingBriefs({ api }) {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showBrief, setShowBrief] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/marketing/briefs"),
        api.get("/marketing/brief-settings"),
      ]);
      setBriefs(b.data || []);
      setSettings(s.data);
    } catch { setBriefs([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/marketing/briefs/generate");
      setBriefs(prev => [r.data, ...prev]);
      setShowBrief(r.data);
    } catch (e) { alert(e.response?.data?.detail || "Erreur de generation"); }
    setGenerating(false);
  };

  const deleteBrief = async (id) => {
    if (!window.confirm("Supprimer ce brief ?")) return;
    await api.delete(`/marketing/briefs/${id}`);
    setBriefs(prev => prev.filter(b => b.brief_id !== id));
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/marketing/brief-settings", settings);
      setShowSettings(false);
    } catch (e) { alert("Erreur"); }
    setSavingSettings(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#10b981]" /></div>;

  return (
    <div data-testid="briefs-tab" className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Newspaper className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold">Briefs Marketing Hebdomadaires</h3>
            <p className="text-[10px] text-slate-500">
              Rapport IA genere chaque {settings?.day || "lundi"} — resume, recommandations et suggestions de posts
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowSettings(true)} variant="outline" className="border-white/10 text-slate-400 text-xs" data-testid="brief-settings-btn">
            <Settings className="w-3 h-3 mr-1" /> Config
          </Button>
          <Button onClick={generate} disabled={generating} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold" data-testid="generate-brief-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Generer le brief
          </Button>
        </div>
      </div>

      {/* Briefs list */}
      {briefs.length === 0 ? (
        <div className="text-center py-16 bg-[#14101c] border border-dashed border-white/10 rounded-xl">
          <Newspaper className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-400 text-sm font-medium">Aucun brief genere</p>
          <p className="text-[10px] text-slate-500 mt-1 mb-4">Generez votre premier brief marketing hebdomadaire</p>
          <Button onClick={generate} disabled={generating} className="bg-amber-500 text-white text-xs font-bold">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Generer maintenant
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {briefs.map(b => {
            const d = b.created_at ? new Date(b.created_at) : null;
            const stats = b.stats || {};
            return (
              <div key={b.brief_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`brief-${b.brief_id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Newspaper className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white">{b.title}</p>
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Share2 className="w-2.5 h-2.5" /> {stats.total_posts || 0} posts
                      </span>
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Users className="w-2.5 h-2.5" /> {stats.new_prospects || 0} prospects
                      </span>
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Target className="w-2.5 h-2.5" /> {stats.new_opps || 0} opportunites
                      </span>
                      {d && (
                        <span className="text-[10px] text-slate-600 flex items-center gap-1">
                          <Calendar className="w-2.5 h-2.5" /> {d.toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => setShowBrief(b)} className="p-1.5 hover:bg-amber-500/10 rounded-lg" title="Voir le brief" data-testid={`view-brief-${b.brief_id}`}>
                      <Eye className="w-3.5 h-3.5 text-amber-400" />
                    </button>
                    <button onClick={() => deleteBrief(b.brief_id)} className="p-1.5 hover:bg-red-500/10 rounded-lg" title="Supprimer">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Brief Detail Dialog */}
      <Dialog open={!!showBrief} onOpenChange={v => { if (!v) setShowBrief(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-amber-400" />
              {showBrief?.title || "Brief Marketing"}
            </DialogTitle>
          </DialogHeader>
          {showBrief && (
            <div className="mt-3">
              {/* Stats bar */}
              <div className="flex gap-3 mb-4 flex-wrap">
                <span className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {showBrief.stats?.total_posts || 0} posts
                </span>
                <span className="text-[10px] px-2.5 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  {showBrief.stats?.new_prospects || 0} prospects
                </span>
                <span className="text-[10px] px-2.5 py-1 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                  {showBrief.stats?.new_opps || 0} opportunites
                </span>
              </div>
              {/* Content rendered as markdown-like */}
              <div className="prose prose-invert prose-sm max-w-none text-sm leading-relaxed whitespace-pre-wrap text-slate-300" data-testid="brief-content">
                {showBrief.content}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" /> Parametres du Brief
            </DialogTitle>
          </DialogHeader>
          {settings && (
            <div className="space-y-4 mt-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1.5 block">Jour de generation</label>
                <select value={settings.day} onChange={e => setSettings(s => ({ ...s, day: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="brief-day-select">
                  {DAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-300">Suggestions de posts</p>
                  <p className="text-[10px] text-slate-500">Inclure des posts pre-rediges</p>
                </div>
                <button onClick={() => setSettings(s => ({ ...s, include_post_suggestions: !s.include_post_suggestions }))}
                  className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${settings.include_post_suggestions ? "bg-[#10b981]" : "bg-slate-600"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${settings.include_post_suggestions ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-300">Recommandations strategiques</p>
                  <p className="text-[10px] text-slate-500">Analyse et conseils IA</p>
                </div>
                <button onClick={() => setSettings(s => ({ ...s, include_strategy: !s.include_strategy }))}
                  className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${settings.include_strategy ? "bg-[#10b981]" : "bg-slate-600"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${settings.include_strategy ? "left-[22px]" : "left-0.5"}`} />
                </button>
              </div>
              <Button onClick={saveSettings} disabled={savingSettings} className="w-full bg-amber-500 text-white font-bold" data-testid="save-brief-settings">
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Sauvegarder
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
